import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { addDays, format } from 'date-fns';
import { isEqual } from 'lodash-es';

import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import Checkbox from 'core/components/checkbox';
import { useStore } from 'core/store/store.mobx';
import { OfferPageTabs } from 'core/store/offer-store';
import { getPolicyType } from 'core/helpers/policy-type';
import useSession from 'core/hooks/use-session';
import useBrandFeatures from 'core/hooks/use-brand-features';
import pencilIcon from 'core/assets/svg/pencil-orange.svg';
import { track } from 'core/helpers/analytics';

import useStyles from '../footer.styles';

const effectiveDateFieldMapper = {
  HA: ['homeEffectiveDate', 'autoEffectiveDate'],
  CA: ['condoEffectiveDate', 'autoEffectiveDate'],
  AR: ['rentersEffectiveDate', 'autoEffectiveDate'],
  H: ['homeEffectiveDate'],
  A: ['autoEffectiveDate'],
  R: ['rentersEffectiveDate'],
  C: ['condoEffectiveDate']
};

const FEDOfferFooter = observer(
  ({
    onUpdate,
    dwellingNeedsUpdate,
    autoNeedsUpdate,
    shouldShowCheckboxAnUpdateButton,
    setShouldShowCheckboxAnUpdateButton,
    dwellingPolicyType,
    autoEffectiveDate,
    dwellingEffectiveDate
  }) => {
    const { isAgency, user } = useSession();
    const { hasFutureEffectiveDateDiscount } = useBrandFeatures();
    const classes = useStyles();
    const { offer: store } = useStore();
    const { setFieldValue, initialValues } = useFormikContext();

    const [effectiveDateType, setEffectiveDateType] = useState(null);
    const [fieldsToUpdate, setFieldsToUpdate] = useState([]);
    const [autoChangedFields, setAutoChangedFields] = useState([]);
    const [isChecked, setIsChecked] = useState(false);

    const { offer, loading } = store;
    const { webUserId } = offer;

    const { isRenters, isHome, isCondo, isAuto, isAnyBundle, hasHome, hasCondo } = getPolicyType(store.selectedOption);
    const {
      autoEffectiveDate: initialAutoEffectiveDate,
      homeEffectiveDate: initialHomeEffectiveDate,
      condoEffectiveDate: initialCondoEffectiveDate,
      rentersEffectiveDate: initialRentersEffectiveDate
    } = initialValues?.global || {};

    let initialDwellingEffectiveDate;
    if (hasHome) {
      initialDwellingEffectiveDate = initialHomeEffectiveDate;
    } else if (hasCondo) {
      initialDwellingEffectiveDate = initialCondoEffectiveDate;
    } else if (isRenters) {
      initialDwellingEffectiveDate = initialRentersEffectiveDate;
    }

    useEffect(() => {
      const fieldsToUpdate = [];
      let updateStringForUI;

      if (isAuto || (isAnyBundle && autoNeedsUpdate && !dwellingNeedsUpdate)) {
        updateStringForUI = 'auto';
        fieldsToUpdate.push(effectiveDateFieldMapper.A);
      } else if (isHome || isCondo || isRenters || (isAnyBundle && dwellingNeedsUpdate && !autoNeedsUpdate)) {
        updateStringForUI = 'dwelling';
        fieldsToUpdate.push(effectiveDateFieldMapper[dwellingPolicyType]);
      } else if (isAnyBundle && dwellingNeedsUpdate && autoNeedsUpdate) {
        updateStringForUI = 'bundle';
        fieldsToUpdate.push(effectiveDateFieldMapper[store.selectedOption]);
      }

      setEffectiveDateType(updateStringForUI);
      setFieldsToUpdate(fieldsToUpdate);
    }, [store.selectedOption, isAuto, isAnyBundle, autoNeedsUpdate, dwellingNeedsUpdate, isHome, isCondo, isRenters]);

    useEffect(() => {
      const bothDatesManuallyChanged =
        !isEqual(initialAutoEffectiveDate, autoEffectiveDate) &&
        !isEqual(initialDwellingEffectiveDate, dwellingEffectiveDate);

      const oneNeedsUpdate = autoNeedsUpdate || dwellingNeedsUpdate;
      if (bothDatesManuallyChanged && !oneNeedsUpdate) {
        setShouldShowCheckboxAnUpdateButton(false);
      } else {
        setShouldShowCheckboxAnUpdateButton(true);
      }
    }, [
      initialDwellingEffectiveDate,
      dwellingEffectiveDate,
      initialAutoEffectiveDate,
      autoEffectiveDate,
      autoNeedsUpdate,
      dwellingNeedsUpdate
    ]);

    const [checkBoxUpdateUsed, setCheckBoxUpdateUsed] = useState(false);
    const handleTabSwitch = () => {
      store.setCurrentTab(OfferPageTabs.SETTINGS);
    };

    const maxFutureEffectiveDate = addDays(new Date(), 8);
    const flatFieldsToUpdate = fieldsToUpdate.flat();
    const appliedMaxFutureEffectiveDateChange = (e) => {
      setIsChecked(e.target.checked);
      if (e.target.checked) {
        const autoUpdatedFields = [];
        flatFieldsToUpdate.forEach((field) => {
          setFieldValue(`global.${field}`, format(maxFutureEffectiveDate, 'yyyy-MM-dd'));
          autoUpdatedFields.push(field);
        });
        setAutoChangedFields(autoUpdatedFields);
        setShouldShowCheckboxAnUpdateButton(true);
        handleTabSwitch();
        setCheckBoxUpdateUsed(true);
      } else {
        autoChangedFields.forEach((field) => {
          setFieldValue(`global.${field}`, initialValues.global[field]);
          setAutoChangedFields([]);
        });
        setCheckBoxUpdateUsed(false);
      }
    };

    const handleOnUpdate = () => {
      onUpdate();
      const effectiveDateChanged =
        !isEqual(initialAutoEffectiveDate, autoEffectiveDate) ||
        !isEqual(initialDwellingEffectiveDate, dwellingEffectiveDate);

      let updateMethodUsed;
      if (effectiveDateChanged) {
        if (checkBoxUpdateUsed && flatFieldsToUpdate.length === 2) {
          updateMethodUsed = 'user used the FED checkbox to update the effective date(s)';
        } else if (checkBoxUpdateUsed && flatFieldsToUpdate.length === 1) {
          updateMethodUsed = 'user updated the effective date(s) by both FED checkbox and manual entry';
        } else {
          updateMethodUsed = 'user manually updated the effective date(s) on the billing tab';
        }
      } else {
        updateMethodUsed = 'no change to effective date';
      }

      track('Staff- Outdated Offer Footer Updated', {
        effectiveDateChanged: effectiveDateChanged,
        updateMethodUsed: updateMethodUsed,
        offerId: store.offer.id,
        staffUsername: user.username,
        isAgency,
        webUserId: webUserId,
        offerType: store.selectedOption
      });
    };

    return (
      <>
        <Grid container>
          <Label className={classes.title} type="subtitle">
            Welcome back—let's update your offer
          </Label>
          {shouldShowCheckboxAnUpdateButton && hasFutureEffectiveDateDiscount && (
            <>
              <Label className={classes.subtitle} type="subtitle">
                Before updating, consider more savings by updating the effective date(s) to 8 days or later from today
                to maximize on our Future Effective Discount.
              </Label>
              <Button className={classes.editContainer} onClick={handleTabSwitch} variant="text" color="secondary">
                <img src={pencilIcon} alt="Orange pencil icon" className={classes.editIcon} />
                <Label type="infoCardText">Update effective date(s)</Label>
              </Button>
            </>
          )}
        </Grid>

        <Grid container alignItems="center" justifyContent="center" spacing={6}>
          {shouldShowCheckboxAnUpdateButton && hasFutureEffectiveDateDiscount && (
            <Grid item>
              <Checkbox
                id="attestation"
                mode="light"
                name="attestation"
                label="Apply maximum future effective date discount"
                description={
                  <>
                    (Sets the {effectiveDateType} effective date to 8 days from today,
                    {format(maxFutureEffectiveDate, 'MM/dd/yyyy')})
                  </>
                }
                value={isChecked}
                onChange={appliedMaxFutureEffectiveDateChange}
                tooltipText="This date maximizes our Future Effective Discount (FED), which is a discount given
        if the effective date is 8 days or later than today.
        Selecting it may lower the price for your member. "
                tooltipLabel="More Info"
              />
            </Grid>
          )}

          <Grid item>
            <Button
              onClick={handleOnUpdate}
              disabled={loading}
              loading={loading}
              variant="contained"
              color="primary"
              className={classes.btn}
            >
              <Label className={classes.btnLabel}>Update offer price</Label>
            </Button>
          </Grid>
        </Grid>
      </>
    );
  }
);

export default FEDOfferFooter;
